import ExpiryMap from 'expiry-map';
import { create } from 'zustand';

import type { PathValue, UserUiState, UserUiStatePath } from '@infinex/types';

import { setPath } from '../utils/index';

const DEFAULT_EXPIRY_MS = 3_000;

type OptimisticUpdate<Path extends UserUiStatePath = UserUiStatePath> = {
  path: Path;
  value: PathValue<UserUiState, Path> | null;
};

type OptimisticUiStateStore = {
  // State
  /** Hack: Increment this to trigger re-renders */
  updateVersion: number; //

  // Actions
  addOptimisticUpdate: <Path extends UserUiStatePath>(
    path: Path,
    value: PathValue<UserUiState, Path> | null
  ) => string;
  removeOptimisticUpdate: (updateId: string) => void;
  makeOptimisticState: (baseUiState: UserUiState | undefined) => UserUiState;
};

/**
 * For context, the reason we use expiries rather than just trusting the next push is because of a scenario like:
 * - A background rebuild of the dataset happens to occur
 * - We do an optimistic update
 * - The WS push from the background rebuild is received and the optimistic state is overwritten ⚠️
 *   - (State becomes incorrect (turns back to original state) here)
 * - The WS push from the correct update comes later ✅
 *   - (State gets fixed)
 *
 * So we choose to ignore pushes before the updates expire to avoid this
 */
const optimisticUpdatesByUpdateId = new ExpiryMap<string, OptimisticUpdate>(
  DEFAULT_EXPIRY_MS
);

/**
 * Store for optimistic UI state updates.
 *
 * Shouldn't need to be used directly, use the query and mutation hooks instead
 */
export const useOptimisticUiStateStore = create<OptimisticUiStateStore>(
  (set) => ({
    // State
    updateVersion: 0,

    // Add an optimistic update
    addOptimisticUpdate: (path, value) => {
      const updateId = crypto.randomUUID();

      optimisticUpdatesByUpdateId.set(updateId, {
        path,
        value,
      });

      // Increment version to trigger re-renders
      set((state) => ({ updateVersion: state.updateVersion + 1 }));

      return updateId;
    },

    // Remove a specific optimistic update
    removeOptimisticUpdate: (updateId) => {
      if (optimisticUpdatesByUpdateId.has(updateId)) {
        optimisticUpdatesByUpdateId.delete(updateId);
        // Increment version to trigger re-renders
        set((state) => ({ updateVersion: state.updateVersion + 1 }));
      }
    },

    // Apply optimistic updates to base UI state
    makeOptimisticState: (baseUiState) => {
      // Ensure we always start from a defined object to avoid spreading undefined
      let result: UserUiState = (baseUiState ?? {}) as UserUiState;

      for (const [, update] of optimisticUpdatesByUpdateId) {
        if (update.value === null) {
          result = setPath(result, update.path, undefined);
        } else {
          result = setPath(result, update.path, update.value);
        }
      }

      return result;
    },
  })
);
